import { useCallback, useMemo } from 'react';

import { useInstantSearch, useSortBy } from 'react-instantsearch';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useSortOptions } from './hooks/useSortOptions';

type Result = ModelHookResult<
	{
		options: ReadonlyArray<{ label: string; value: string; subtitle: string }>;
		sortByTitle: string;
		currentRefinement: string;
		currentLabel: string;
	},
	{
		// FIXME(SelectMenu): Using string | number because the component requires Key type from react-aria as value
		changeSelection: (value: string | number | null) => void;
	}
>;

export function useSortSelector(): Result {
	const { translate } = useI18n();

	const sortByTitle = useMemo(() => translate('corgi.search.sort.title'), [translate]);

	const {
		data: { options },
	} = useSortOptions();

	const { canRefine, currentRefinement, refine } = useSortBy({
		items: options.map(({ label, value }) => ({ label, value })),
	});

	const track = useTracking();

	const { indexUiState } = useInstantSearch();

	const getTrackingContext = useCallback(
		(selectedValue: string) => {
			const selectedOption = options.find(({ value }) => value === selectedValue);
			return selectedOption?.trackingContext || '';
		},
		[options],
	);

	// FIXME(SelectMenu): Using string | number because the component requires Key type from react-aria as value
	const changeSelection = useCallback(
		(selectedKey: string | number | null) => {
			if (selectedKey === null) return;
			const selectedOption = `${selectedKey}`;
			if (!selectedOption) return;
			if (canRefine) {
				refine(selectedOption);
				void track('search', { context: getTrackingContext(selectedOption), q: indexUiState.query || '' });
			}
		},
		[canRefine, getTrackingContext, indexUiState.query, refine, track],
	);

	const { label: currentLabel } = useMemo(() => {
		const selectedOption = options.find(({ value }) => value === currentRefinement);
		return selectedOption || { label: '' };
	}, [currentRefinement, options]);

	return {
		data: {
			options: options.map(({ label, value, subtitle }) => ({ label, value, subtitle })),
			sortByTitle,
			currentRefinement,
			currentLabel,
		},
		actions: {
			changeSelection,
		},
	};
}
